/**
 * Sahifani «ko'rish» va boshqarish: AI yordamchi buyruqlari shu yerda bajariladi.
 *
 * `snapshot()` ko'rinadigan interaktiv elementlarni raqamlab, modelga ixcham
 * matn sifatida beradi. Model `click(7)` desa — aynan shu raqamli element
 * bosiladi. Raqamlar faqat oxirgi `snapshot()` uchun amal qiladi: har
 * harakatdan keyin sahifa qaytadan «suratga» olinadi.
 *
 * Harakat odatiy foydalanuvchi ishiga o'xshab bajariladi (elementga suriladi,
 * ajratib ko'rsatiladi, so'ng bosiladi), shu sababli foydalanuvchi ekranda
 * nima bo'layotganini kuzatib turadi.
 */
const SELECTOR = [
  "a[href]",
  "button",
  "input:not([type=hidden])",
  "select",
  "textarea",
  "summary",
  "[role=button]",
  "[role=tab]",
  "[role=menuitem]",
  "[role=link]",
  "[role=checkbox]",
].join(",");

const MAX_ELEMENTS = 80;
const RISKY = /o'chir|удал|delete|to'la|оплат|pay\b|yubor|отправ|submit|tasdiqla|подтверд|bekor|отмен|chiqish|chiqing|выйти|log ?out|sign ?out/i;

let registry = new Map<number, HTMLElement>();

const clean = (value: string | null | undefined, max = 70) =>
  (value ?? "").replace(/\s+/g, " ").trim().slice(0, max);

function isVisible(el: HTMLElement): boolean {
  if (el.closest(".ai-dock, [aria-hidden=true], [hidden], [inert]")) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return false;
  const style = getComputedStyle(el);
  return style.visibility !== "hidden" && style.display !== "none" && style.opacity !== "0";
}

function labelOf(el: HTMLElement): string {
  const aria = clean(el.getAttribute("aria-label"));
  if (aria) return aria;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    const byFor = el.id ? document.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(el.id)}"]`) : null;
    const wrapped = el.closest("label");
    return clean(byFor?.textContent || wrapped?.textContent || el.getAttribute("placeholder") || el.name);
  }
  return clean(el.innerText || el.textContent) || clean(el.getAttribute("title"));
}

export function isClickable(el: HTMLElement): boolean {
  return !(el as HTMLButtonElement).disabled && el.getAttribute("aria-disabled") !== "true";
}

/** Joriy sahifaning raqamlangan ro'yxati (modelga yuboriladi). */
export function snapshot(): { url: string; elements: string } {
  registry = new Map();
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(SELECTOR)).filter(isVisible);

  // Ekranda ko'ringanlar birinchi — uzun sahifalarda ro'yxat to'lib ketmasin.
  const inView = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight;
  };
  const ordered = [...candidates.filter(inView), ...candidates.filter((el) => !inView(el))].slice(0, MAX_ELEMENTS);

  const heading = clean(document.querySelector("main h1, h1")?.textContent, 120);
  const lines: string[] = [];
  if (heading) lines.push(`Sarlavha: ${heading}`);

  ordered.forEach((el, index) => {
    const id = index + 1;
    registry.set(id, el);
    const label = labelOf(el);
    const off = isClickable(el) ? "" : " (o'chirilgan)";
    if (el instanceof HTMLSelectElement) {
      const options = Array.from(el.options).slice(0, 12).map((o) => clean(o.text, 30)).join(" | ");
      lines.push(`[${id}] tanlov "${label}" hozir="${clean(el.selectedOptions[0]?.text, 30)}" variantlar: ${options}${off}`);
    } else if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) {
      lines.push(`[${id}] ${el.type} "${label}" ${el.checked ? "belgilangan" : "belgilanmagan"}${off}`);
    } else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      const value = el.type === "password" ? "" : clean(el.value, 40);
      lines.push(`[${id}] maydon "${label}"${value ? ` qiymat="${value}"` : ""}${off}`);
    } else if (el instanceof HTMLAnchorElement) {
      lines.push(`[${id}] havola "${label}" → ${clean(el.getAttribute("href"), 60)}${off}`);
    } else {
      lines.push(`[${id}] tugma "${label}"${off}`);
    }
  });

  return { url: location.pathname + location.search, elements: lines.join("\n") };
}

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/** Elementga suradi va qisqa muddat ajratib ko'rsatadi. */
async function reveal(el: HTMLElement): Promise<void> {
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  await wait(350);
  el.classList.add("ai-target");
  await wait(550);
}

function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
}

export interface ActionResult {
  ok: boolean;
  /** Modelga qaytadigan qisqa natija. */
  message: string;
  /** Foydalanuvchiga ko'rsatiladigan qadam. */
  trace: string;
}

export interface ActionHost {
  navigate: (path: string) => void;
  allowedPaths: string[];
  confirm: (question: string) => Promise<boolean>;
}

function target(id: unknown): HTMLElement | null {
  const el = registry.get(Number(id));
  return el && el.isConnected ? el : null;
}

export async function perform(name: string, args: Record<string, unknown>, host: ActionHost): Promise<ActionResult> {
  const missing = (): ActionResult => ({
    ok: false,
    message: "Bunday element yo'q yoki sahifa o'zgargan. Yangi ro'yxatdan tanlang.",
    trace: "Element topilmadi",
  });

  try {
    if (name === "navigate") {
      const path = String(args.path ?? "");
      if (!host.allowedPaths.includes(path)) {
        return { ok: false, message: "Bu sahifaga o'tish mumkin emas.", trace: `Ruxsat yo'q: ${path}` };
      }
      host.navigate(path);
      await wait(1200);
      return { ok: true, message: `${path} ochildi.`, trace: `Sahifa ochildi: ${path}` };
    }

    if (name === "scroll") {
      const dy = (args.direction === "up" ? -1 : 1) * Math.round(window.innerHeight * 0.8);
      window.scrollBy({ top: dy, behavior: "smooth" });
      document.querySelector("main")?.scrollBy({ top: dy, behavior: "smooth" });
      await wait(500);
      return { ok: true, message: "Sahifa aylantirildi.", trace: "Sahifa aylantirildi" };
    }

    const el = target(args.id);
    if (!el) return missing();
    const label = labelOf(el);
    if (!isClickable(el)) return { ok: false, message: "Element o'chirilgan.", trace: `«${label}» o'chirilgan` };

    if (name === "click") {
      if (RISKY.test(label) && !(await host.confirm(`AI «${label}» tugmasini bosmoqchi. Ruxsat berasizmi?`))) {
        return { ok: false, message: "Foydalanuvchi rad etdi.", trace: `«${label}» — bekor qilindi` };
      }
      await reveal(el);
      el.click();
      await wait(900);
      return { ok: true, message: `«${label}» bosildi.`, trace: `Bosildi: «${label}»` };
    }

    if (name === "type_text") {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) || (el as HTMLInputElement).type === "password") {
        return { ok: false, message: "Bu maydonga yozib bo'lmaydi.", trace: `«${label}» ga yozib bo'lmaydi` };
      }
      await reveal(el);
      el.focus();
      setNativeValue(el, String(args.text ?? ""));
      await wait(500);
      return { ok: true, message: `«${label}» maydoni to'ldirildi.`, trace: `Yozildi: «${label}»` };
    }

    if (name === "select_option") {
      if (!(el instanceof HTMLSelectElement)) return missing();
      const wanted = String(args.option ?? "").toLowerCase();
      const option = Array.from(el.options).find(
        (o) => o.text.toLowerCase().includes(wanted) || o.value.toLowerCase() === wanted,
      );
      if (!option) return { ok: false, message: "Bunday variant yo'q.", trace: `Variant topilmadi: ${wanted}` };
      await reveal(el);
      Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value")?.set?.call(el, option.value);
      el.dispatchEvent(new Event("change", { bubbles: true }));
      await wait(700);
      return { ok: true, message: `«${option.text}» tanlandi.`, trace: `Tanlandi: «${option.text}»` };
    }
  } finally {
    registry.forEach((node) => node.classList.remove("ai-target"));
  }
  return { ok: false, message: "Noma'lum harakat.", trace: name };
}
